// Client-side port of the balance/filter/sort/group/cash-flow-status logic
// that used to live server-side in express-server/controllers/
// transaction-controller.js (getTransactionsByEntityId + computeCashFlowStatus
// + groupTransactions). The server now only filters by real/recurring/
// one-time/bank/category and returns the entire matching set in one shot;
// everything else below runs in the browser instead. Kept as a line-by-line
// port (not a rewrite) so behavior stays identical to what was validated
// server-side.

export type TransactionRecord = Record<string, any>;

export type FilterConfig = {
    descriptionKeyword?: string;
    // Header search box: separate from the description column filter, and matches wider.
    searchKeyword?: string;
    amountKeyword?: [number, number];
    sortBy?: 'date' | 'amount' | 'description' | 'category' | 'bank';
    sortDirection?: 'ASC' | 'DESC';
};

const arrayMax = (arr: number[]): number => arr.reduce((max, v) => (v > max ? v : max), arr[0]);
const arrayMin = (arr: number[]): number => arr.reduce((min, v) => (v < min ? v : min), arr[0]);

// Port of the SQL window function (`:openingBalance + SUM(amount) OVER
// (ORDER BY date ASC, sequenceId ASC ROWS UNBOUNDED PRECEDING)`) - sorts
// ascending and walks a cumulative sum, same as the server's SQL/JS balance
// walk. Returns a new array in ascending (date, sequenceId) order - matching
// what the SQL fetch used to hand back before any display sort was applied.
export function computeRunningBalance(transactions: TransactionRecord[], totalOpeningBalance: number, includePushedForwardInBalance: boolean = true): TransactionRecord[] {
    const sorted = [...transactions].sort((a, b) => {
        const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
        return dateCompare !== 0 ? dateCompare : (a.sequenceId || 0) - (b.sequenceId || 0);
    });

    let runningBalance = Number(totalOpeningBalance);
    return sorted.map(tx => {
        // Mirrors the server's PUSHED_FORWARD_JSON_CHECK / buildStatusTransactions
        // JS fallback: a provision pushed forward by the aging job still shows
        // up as a row (with a real running-balance value), it just doesn't
        // contribute its own amount to the running total when the entity has
        // opted out via "Include pushed-forward provisions in the balance
        // calculation" - see the "Include pushed-forward..." toggle in
        // provision-settings.tsx.
        const isPushedForward = tx.type === 'provision' && tx.sourceData?.pushedForwardForAging === true;
        if (includePushedForwardInBalance || !isPushedForward) {
            runningBalance += Number(tx.amount) || 0;
        }
        return { ...tx, balance: Math.round(runningBalance * 100) / 100, excludedFromBalance: !includePushedForwardInBalance && isPushedForward };
    });
}

// The header search box matches description, amount, and date - amount and
// date as substring matches against a few common textual representations,
// since a user typing "50" or "04/09" shouldn't have to know which exact
// format/precision the underlying value is stored in.
const matchesKeyword = (tx: TransactionRecord, keyword: string): boolean => {
    const desc = tx.description?.toLowerCase() || '';
    return desc.includes(keyword);
};

// Header search: description, original bank description, invoice number, bank account name, or amount as typed.
const matchesSearch = (tx: TransactionRecord, keyword: string, bankNames?: Map<string, string>): boolean => {
    const bankName = bankNames?.get(tx.bankAccountId);
    const fields = [tx.description, tx.originalDescription, tx.invoiceNumber, bankName, tx.amount];
    return fields.some(v => v !== null && v !== undefined && String(v).toLowerCase().includes(keyword));
};

// Port of getTransactionsByEntityId's "DISPLAY-ONLY FILTERS" block.
export function applyDisplayFilters(
    transactions: TransactionRecord[],
    { importId, startDate, endDate, filterConfig, bankNames }: { importId?: string | string[]; startDate?: Date | null; endDate?: Date | null; filterConfig?: FilterConfig | null; bankNames?: Map<string, string> }
): TransactionRecord[] {
    let displayTransactions = transactions;

    if (importId) {
        const importIdArray = typeof importId === 'string'
            ? importId.split(',').map(id => id.trim())
            : Array.isArray(importId) ? importId : [importId];

        displayTransactions = displayTransactions.filter(d => {
            if (importIdArray.includes('null')) {
                return d.importId === null || (d.importId && importIdArray.includes(d.importId.toString()));
            }
            return d.importId && importIdArray.includes(d.importId.toString());
        });
    }

    if (startDate && endDate) {
        displayTransactions = displayTransactions.filter(d => {
            const date = new Date(d.date);
            return date >= startDate && date <= endDate;
        });
    }

    if (filterConfig) {
        const { descriptionKeyword, amountKeyword, searchKeyword } = filterConfig;

        if (descriptionKeyword && descriptionKeyword.trim()) {
            const keyword = descriptionKeyword.toLowerCase().trim();
            displayTransactions = displayTransactions.filter(tx => matchesKeyword(tx, keyword));
        }

        if (searchKeyword && searchKeyword.trim()) {
            const keyword = searchKeyword.toLowerCase().trim();
            displayTransactions = displayTransactions.filter(tx => matchesSearch(tx, keyword, bankNames));
        }

        if (amountKeyword && Array.isArray(amountKeyword) && amountKeyword.length === 2) {
            const [min, max] = amountKeyword;
            displayTransactions = displayTransactions.filter(tx => {
                const amount = Number(tx.amount) || 0;
                return amount >= min && amount <= max;
            });
        }
    }

    return displayTransactions;
}

// Port of the ungrouped-view branch of getTransactionsByEntityId's "SORT"
// block (the grouped-view branch is `groupTransactionsClient` below).
export function sortTransactions(transactions: TransactionRecord[], filterConfig?: FilterConfig | null): TransactionRecord[] {
    const sorted = [...transactions];

    if (filterConfig && filterConfig.sortBy) {
        const { sortBy, sortDirection } = filterConfig;
        const sortDir = sortDirection || 'DESC';

        if (sortBy === 'date') {
            return sorted.sort((a, b) => {
                const dateCompare = sortDir === 'ASC'
                    ? new Date(a.date).getTime() - new Date(b.date).getTime()
                    : new Date(b.date).getTime() - new Date(a.date).getTime();
                return dateCompare !== 0 ? dateCompare : (sortDir === 'ASC' ? (a.sequenceId || 0) - (b.sequenceId || 0) : (b.sequenceId || 0) - (a.sequenceId || 0));
            });
        } else if (sortBy === 'amount') {
            return sorted.sort((a, b) => {
                return sortDir === 'ASC'
                    ? (Number(a.amount) || 0) - (Number(b.amount) || 0)
                    : (Number(b.amount) || 0) - (Number(a.amount) || 0);
            });
        } else if (sortBy === 'description') {
            return sorted.sort((a, b) => {
                const descA = a.description?.toLowerCase() || '';
                const descB = b.description?.toLowerCase() || '';
                return sortDir === 'ASC' ? descA.localeCompare(descB) : descB.localeCompare(descA);
            });
        } else if (sortBy === 'category') {
            return sorted.sort((a, b) => {
                const catA = a.category?.toLowerCase() || '';
                const catB = b.category?.toLowerCase() || '';
                return sortDir === 'ASC' ? catA.localeCompare(catB) : catB.localeCompare(catA);
            });
        } else if (sortBy === 'bank') {
            return sorted.sort((a, b) => {
                const bankA = a.bank || '';
                const bankB = b.bank || '';
                const isNoBankA = bankA === 'No Bank Account';
                const isNoBankB = bankB === 'No Bank Account';
                if (isNoBankA && !isNoBankB) return sortDir === 'ASC' ? -1 : 1;
                if (!isNoBankA && isNoBankB) return sortDir === 'ASC' ? 1 : -1;
                return sortDir === 'ASC'
                    ? bankA.toLowerCase().localeCompare(bankB.toLowerCase())
                    : bankB.toLowerCase().localeCompare(bankA.toLowerCase());
            });
        }
        return sorted.sort((a, b) => {
            const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
            return dateCompare !== 0 ? dateCompare : (b.sequenceId || 0) - (a.sequenceId || 0);
        });
    }

    return sorted.sort((a, b) => {
        const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
        return dateCompare !== 0 ? dateCompare : (b.sequenceId || 0) - (a.sequenceId || 0);
    });
}

// Port of the server's groupTransactions (day/week/month/quarter/year
// bucketing). `startDayOfWeek` replaces the server's EntitySettings lookup -
// pass companySettings.settings.startDayOfWeek from useSettings().
export function groupTransactionsClient(
    transactions: TransactionRecord[],
    viewBy: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'transactions',
    startDayOfWeek: string | null | undefined,
    sortDirection: 'ASC' | 'DESC' = 'DESC'
): TransactionRecord[] {
    if (viewBy === 'transactions') {
        return transactions;
    }

    const dayMap: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
    };
    const startDay = dayMap[startDayOfWeek?.toLowerCase() || ''] ?? 1; // default to Monday

    const sorted = [...transactions].sort(
        (a, b) => sortDirection === 'ASC'
            ? new Date(a.date).getTime() - new Date(b.date).getTime()
            : new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    let getGroupKey: (date: Date) => string;
    let getLabel: (date: Date) => string;

    switch (viewBy) {
        case 'day':
            getGroupKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            getLabel = date => getGroupKey(date);
            break;

        case 'week':
            getGroupKey = date => {
                const startOfWeek = new Date(date);
                const currentDay = date.getDay();
                const daysToSubtract = (currentDay - startDay + 7) % 7;
                const diff = date.getDate() - daysToSubtract;
                startOfWeek.setDate(diff);
                return `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
            };
            getLabel = date => {
                const startOfWeek = new Date(date);
                const currentDay = date.getDay();
                const daysToSubtract = (currentDay - startDay + 7) % 7;
                const diff = date.getDate() - daysToSubtract;
                startOfWeek.setDate(diff);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return `${getGroupKey(date)} to ${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, '0')}-${String(endOfWeek.getDate()).padStart(2, '0')}`;
            };
            break;

        case 'month':
            getGroupKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            getLabel = date => {
                const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                return `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-${String(startOfMonth.getDate()).padStart(2, '0')} to ${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;
            };
            break;

        case 'quarter':
            getGroupKey = date => `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
            getLabel = date => {
                const quarter = Math.floor(date.getMonth() / 3) + 1;
                const startMonth = (quarter - 1) * 3;
                const startOfQuarter = new Date(date.getFullYear(), startMonth, 1);
                const endOfQuarter = new Date(date.getFullYear(), startMonth + 3, 0);
                return `${startOfQuarter.getFullYear()}-${String(startOfQuarter.getMonth() + 1).padStart(2, '0')}-${String(startOfQuarter.getDate()).padStart(2, '0')} to ${endOfQuarter.getFullYear()}-${String(endOfQuarter.getMonth() + 1).padStart(2, '0')}-${String(endOfQuarter.getDate()).padStart(2, '0')}`;
            };
            break;

        case 'year':
            getGroupKey = date => date.getFullYear().toString();
            getLabel = date => getGroupKey(date);
            break;

        default:
            getGroupKey = () => '';
            getLabel = () => '';
    }

    const map = new Map<string, TransactionRecord & { latestTransactionDate: any }>();

    sorted.forEach(tx => {
        const date = new Date(tx.date);
        const key = getGroupKey(date);

        if (!map.has(key)) {
            map.set(key, {
                ...tx,
                amount: 0,
                date: getLabel(date),
                bankId: null,
                categoryId: null,
                balance: 0,
                latestTransactionDate: tx.date,
            });
        }

        const grouped = map.get(key)!;
        grouped.amount = Number(grouped.amount) + Number(tx.amount);

        if (new Date(tx.date) >= new Date(grouped.latestTransactionDate)) {
            grouped.balance = tx.balance;
            // Travels with balance, not with the group's initial {...tx}
            // spread (whichever transaction happened to create the map
            // entry) - otherwise a group's displayed "-" vs real number
            // could come from a different transaction than the one whose
            // balance it's actually showing.
            grouped.excludedFromBalance = tx.excludedFromBalance;
            grouped.latestTransactionDate = tx.date;
        }
    });

    return Array.from(map.values());
}

export function computeHighLow(transactions: TransactionRecord[]): { highestAmount: number; lowestAmount: number } {
    const amounts = transactions.map(tx => Number(tx.amount) || 0);
    return {
        highestAmount: amounts.length > 0 ? arrayMax(amounts) : 0,
        lowestAmount: amounts.length > 0 ? arrayMin(amounts) : 0,
    };
}

export type CashFlowStatus = {
    cashflowMessage: string;
    wentNegative: boolean;
    daysBeforeNegative: number | '∞';
    todayOpeningBalance: number;
    todayClosingBalance: number;
    todayTransactions: TransactionRecord[];
    firstNegativeDate: string | null;
    firstNegativeTransaction: { guid: string; date: any; balance: number } | null;
};

// Port of computeCashFlowStatus's pure algorithm (the part that isn't
// req/res plumbing) - takes the same balance-walked, real/recurring/
// one-time/bank/category-filtered array the server used to fetch fresh via
// its own buildStatusTransactions call, i.e. `computeRunningBalance`'s
// output BEFORE any display filters (date range/importId/description/
// amount) are applied - "will you run out of money" must not depend on
// what date range happens to be visible.
export function computeCashFlowStatusClient(transactionsWithBalance: TransactionRecord[], totalOpeningBalance: number): CashFlowStatus {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pastTxs = transactionsWithBalance.filter(tx => {
        const txDate = new Date(tx.date);
        txDate.setHours(0, 0, 0, 0);
        return txDate < today;
    });

    let balanceBeforeToday = Number(totalOpeningBalance);
    if (pastTxs.length > 0) {
        balanceBeforeToday = Number(pastTxs[pastTxs.length - 1].balance);
    }

    const todayTransactions = transactionsWithBalance.filter(tx => {
        const txDate = new Date(tx.date);
        txDate.setHours(0, 0, 0, 0);
        return txDate.getTime() === today.getTime();
    });

    let todayOpeningBalance = balanceBeforeToday;
    if (todayTransactions.length > 0) {
        const earliestTodayTx = todayTransactions.reduce((earliest, tx) =>
            (tx.sequenceId || 0) < (earliest.sequenceId || 0) ? tx : earliest
        );
        // Only back out the transaction's own amount if it actually
        // contributed to the running total - a pushed-forward provision
        // excluded from the balance (see computeRunningBalance) already left
        // .balance unchanged from balanceBeforeToday, so subtracting its
        // amount again would understate today's opening balance.
        todayOpeningBalance = earliestTodayTx.excludedFromBalance
            ? Number(earliestTodayTx.balance)
            : Number(earliestTodayTx.balance) - Number(earliestTodayTx.amount);
    }

    const todayClosingBalance = todayTransactions.length > 0
        ? Number(todayTransactions[todayTransactions.length - 1].balance)
        : balanceBeforeToday;

    const hasTransactionsToday = todayTransactions.length > 0;
    const hasNegativeBalanceToday = todayTransactions.some(tx => Number(tx.balance) <= 0);
    const alreadyNegative = balanceBeforeToday <= 0 && !hasTransactionsToday;

    let firstNegStatus: TransactionRecord | null = null;
    for (let i = 0; i < transactionsWithBalance.length && !firstNegStatus; i++) {
        const tx = transactionsWithBalance[i];
        const txDate = new Date(tx.date);
        txDate.setHours(0, 0, 0, 0);
        if (txDate >= today && Number(tx.balance) <= 0) {
            firstNegStatus = tx;
        }
    }

    let cashflowMessage = 'Your company never runs out of money.';

    if (transactionsWithBalance.length === 0) {
        cashflowMessage = 'No transactions available to determine cash flow status.';
    } else if (alreadyNegative) {
        cashflowMessage = 'Your company has run out of money.';
    } else if (hasTransactionsToday && hasNegativeBalanceToday) {
        cashflowMessage = 'Your company runs out of money today.';
    } else if (firstNegStatus) {
        const isoDate = new Date(firstNegStatus.date).toISOString().split('T')[0];
        cashflowMessage = `Your company runs out of money on | ${isoDate}`;
    }

    const wentNegative = alreadyNegative
        || (hasTransactionsToday && hasNegativeBalanceToday)
        || firstNegStatus !== null;

    let daysBeforeNegative: number | '∞';
    if (alreadyNegative || (hasTransactionsToday && hasNegativeBalanceToday)) {
        daysBeforeNegative = 0;
    } else if (firstNegStatus?.date) {
        const negDate = new Date(firstNegStatus.date);
        negDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((negDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        daysBeforeNegative = diffDays >= 0 ? diffDays : 0;
    } else {
        daysBeforeNegative = '∞';
    }

    const firstNegativeDate = firstNegStatus ? new Date(firstNegStatus.date).toISOString() : null;
    const firstNegativeTransaction = firstNegStatus
        ? { guid: firstNegStatus.guid, date: firstNegStatus.date, balance: Number(firstNegStatus.balance) }
        : null;

    return {
        cashflowMessage,
        wentNegative,
        daysBeforeNegative,
        todayOpeningBalance,
        todayClosingBalance,
        todayTransactions,
        firstNegativeDate,
        firstNegativeTransaction,
    };
}
